import { PlusIcon } from "lucide-react";
import { Leaderboard } from "../Dashboard/DashboardComponents/DashboardOrganism/Leaderboard";
import { Button } from "@/shadcnComponents/ui/button";
import { IoHeartOutline } from "react-icons/io5";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaHeart } from "react-icons/fa";
import mixpanel from "mixpanel-browser";
import { getRandomProfileEmoji } from "@/Helpers/helper";
import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;

const useIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      observer.current = new IntersectionObserver(callback, options);
      observer.current.observe(containerRef.current);
    }

    return () => {
      observer.current?.disconnect();
    };
  }, [callback, options]);

  return containerRef;
};

interface VideoProps {
  src: string;
  className?: string;
}

export const Video: React.FC<VideoProps> = ({ src, className }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleIntersection: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      // for auto play and pause
      // if (entry.isIntersecting) {
      //     // Play the video if it is in the viewport
      //     videoRef.current?.play();
      // } else {
      //     // Pause the video if it is out of the viewport
      //     videoRef.current?.pause();
      // }

      // for auto pause only
      if (!entry.isIntersecting && !videoRef.current?.paused) {
        videoRef.current?.pause();
      }
    });
  };

  const containerRef = useIntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: "0px",
    threshold: 0.5,
  });

  return (
    <div ref={containerRef}>
      <video
        ref={videoRef}
        src={src}
        controls
        width="100%"
        className={className}
      />
    </div>
  );
};

type FeedPostCardProps = {
  influencerName: string;
  influencerProfilePic: string;
  brandName: string;
  textContent: string;
  imageContent: string;
  posturl: string;
  onKudos?: () => void;
  isFollowing: boolean;
  isKudos: boolean;
  onFollow?: () => void;
  videoLink?: string;
  onProfileImageClick?: () => void;
};

const FeedPostCard = ({
  influencerName,
  influencerProfilePic,
  brandName,
  textContent,
  imageContent,
  posturl,
  onKudos,
  isFollowing,
  isKudos,
  onFollow,
  videoLink,
  onProfileImageClick,
}: FeedPostCardProps) => {
  return (
    <div className="w-full h-[max-content] flex px-[16px] py-[12px] flex-col justify-end items-start gap-[20px] rounded-[6px] bg-[rgba(38,_38,_38,_0.44)] shadow-md">
      {/* user profile header */}
      <div className="w-full flex justify-between text-[#FFF] font-[Inter]">
        <div className="flex gap-2 items-center">
          <img
            src={influencerProfilePic}
            alt=""
            className="size-[40px] rounded-full flex-shrink-0 cursor-pointer"
            onError={(e: any) => {
              const randomProfileEmoji = getRandomProfileEmoji();
              e.target.onerror = null;
              e.target.src = randomProfileEmoji;
            }}
            onClick={() => onProfileImageClick()}
          />
          <div className="flex flex-col">
            <span className="text-[14px] not-italic font-semibold leading-[normal]">
              {influencerName}
            </span>
            <div className="text-[#666] text-[12px] leading-[normal]">
              collaborated with <b>{brandName}</b>
            </div>
          </div>
        </div>
        <div className=" flex items-center justify-end">
          {!isFollowing ? (
            <Button
              size="sm"
              className="justify-center bg-transparent md:border md:border-[#DC2627] rounded-[10px] text-[#DC2627] gap-2 items-center hover:bg-[#dc2627] hover:text-[#1e1e1e]"
              onClick={() => onFollow()}
            >
              <PlusIcon size={20} />
              Follow
            </Button>
          ) : (
            <div className="text-[#A3A3A3] px-3 text-sm">Following</div>
          )}
        </div>
      </div>

      {/* Body content */}
      <div className="flex flex-col gap-5 w-full">
        {/* text content */}
        <pre className="font-[Inter] overflow-hidden text-[#A3A3A3]  text-wrap  text-[12px] leading-[normal]">
          {textContent}
        </pre>

        {/* media content - max-h-[50vh]*/}
        {imageContent && imageContent?.length > 0 && (
          <div className="relative w-full overflow-hidden flex justify-center items-center mb-2">
            <img
              src={imageContent}
              alt=""
              className="w-full h-auto object-contain rounded-2xl"
            />
          </div>
        )}

        {/* video content */}
        {videoLink && videoLink?.length > 0 && (
          <div className="relative w-full max-h-[80vh] overflow-hidden flex justify-center items-center mb-2">
            <Video
              src={videoLink}
              className="max-w-full max-h-[80vh] h-auto w-auto object-contain rounded-2xl"
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="w-full flex justify-between items-center">
        <div
          className="bg-transparent text-[#DC2627] text-[12px] flex items-center gap-2 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300 cursor-pointer hover:bg-transparent"
          onClick={() => onKudos()}
        >
          {!isKudos ? (
            <>
              <IoHeartOutline size={24} /> Give Kudos
            </>
          ) : (
            <>
              <FaHeart size={24} /> Kudos Given{" "}
            </>
          )}
        </div>
        <div
          className="text-[#A3A3A3] text-[12px] cursor-pointer hover:text-[#6d6d6d]"
          onClick={() => {
            mixpanel.track("Influencer Read Full Post", {
              influencerName,
              posturl,
            });
            window.open(posturl, "_blank");
          }}
        >
          Read Full Post
        </div>
      </div>
    </div>
  );
};

export const InfluencerFeed: React.FC = () => {
  const initialize = useRef(false);
  // const [feeds, setFeeds] = useState([]);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const handleKudos = async (collabId: string, index?: number) => {
    try {
      if (
        posts.find((feed) => feed?.collabData?.collabId === collabId)?.postData
          ?.isKudos
      ) {
        mixpanel.track("Influencer Kudos Removed", {
          collabId,
          feed_post_position: index + 1,
        });

        // code for removing kudos - unKudos
        const res = await axios.post(
          `${host}/influencer/post/unkudos`,
          { collabId },
          { headers: { jwtToken: localStorage.getItem("jwtToken") } }
        );
        const { data } = res;
        if (data?.success) {
          setPosts(
            posts.map((feed) =>
              feed?.collabData?.collabId === collabId
                ? {
                    ...feed,
                    postData: {
                      ...feed.postData,
                      isKudos: !feed?.postData?.isKudos,
                    },
                  }
                : feed
            )
          );
        }
      } else {
        mixpanel.track("Influencer Kudos Given", {
          collabId,
          feed_post_position: index + 1,
        });

        const res = await axios.post(
          `${host}/influencer/post/kudos`,
          { collabId, source: "feed" },
          { headers: { jwtToken: localStorage.getItem("jwtToken") } }
        );
        const { data } = res;
        if (data?.success) {
          setPosts(
            posts.map((feed) =>
              feed?.collabData?.collabId === collabId
                ? {
                    ...feed,
                    postData: {
                      ...feed.postData,
                      isKudos: !feed?.postData?.isKudos,
                    },
                  }
                : feed
            )
          );
        }
      }
    } catch (error) {
      console.log(
        "Error giving kudos:",
        error?.response ? error?.response?.data : error?.message
      );
    }
  };

  const handleFollow = async (influencerId: string, index?: number) => {
    try {
      if (
        posts.find(
          (feed) => feed?.influencerData?.influencerId === influencerId
        )?.influencerData?.isFollowing
      ) {
        // code for removing follow - unfollow
      } else {
        mixpanel.track("Influencer Followed", {
          following_influencerId: influencerId,
          feed_post_position: index + 1,
        });

        const res = await axios.post(
          `${host}/influencer/followCreator`,
          { creatorId: influencerId, source: "feed" },
          { headers: { jwtToken: localStorage.getItem("jwtToken") } }
        );
        const { data } = res;
        if (data?.success) {
          setPosts(
            posts.map((feed) =>
              feed?.influencerData?.influencerId === influencerId
                ? {
                    ...feed,
                    influencerData: {
                      ...feed.influencerData,
                      isFollowing: !feed?.influencerData?.isFollowing,
                    },
                  }
                : feed
            )
          );
        }
      }
    } catch (error) {
      console.log(
        "Error following influencer:",
        error?.response ? error?.response?.data : error?.message
      );
    }
  };

  const fetchPosts = async (page, limit) => {
    setPaginationLoading(true);
    try {
      const response = await axios.post(
        `${host}/influencer/feed`,
        {
          fetchedIds: posts.map((post) => post?.collabData?.collabId),
        },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
          params: {
            page,
            limit,
            // fetchedIds: posts.map(post => post.collabId),
          },
        }
      );
      const newPosts = response.data.feeds;
      setPosts((prevPosts) => [...prevPosts, ...newPosts]);
      setHasMore(newPosts.length > 0);
      setPaginationLoading(false);
    } catch (error) {
      console.error("Error fetching posts:", error);
      setPaginationLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);

    mixpanel.track("Influencer Feed Load More", {
      page,
      viewed_posts: posts.length,
    });

    fetchPosts(page, 10);
  };

  const handleAddPastCollab = () => {
    mixpanel.track("Influencer Add Past Collab");

    window.open("/influencer/build_profile?page=collab", "_blank");
  };

  const handleOpenProfile = (influencerName, slug, index) => {
    mixpanel.track("Influencer Profile Opened", {
      influencerName,
      slug,
      feed_post_position: index + 1,
    });

    window.open(`/${slug}?utm_source=collab_feed`, "_blank");
  };

  useEffect(() => {
    if (!initialize.current) {
      initialize.current = true;

      mixpanel.track("Influencer Feed Page Loaded");

      fetchPosts(page, 10);
      setPage((prevPage) => prevPage + 1);
    }
  }, []);

  return (
    <>
      <MobileMenuBar />

      <div className="w-full relative h-max items-start flex p-[16px] md:p-[40px] pb-[100px] md:pb-[16px] gap-[15px] md:gap-[40px] text-[#A3A3A3] font-[Inter]">
        <div className="w-full md:w-[80%] flex flex-wrap gap-[30px]">
          <div className="flex flex-col items-start w-full gap-1.5 ">
            <span className="text-[color:var(--Shade-White,#FFF)] text-base not-italic font-bold leading-5">
              Collaboration Feed
            </span>
            <span className=" text-[#ADADAD] text-xs not-italic font-normal leading-5">
              Are you an aspiring creator? See how other creators craft
              collaboration posts and take inspiration. <br />
              If you’re experienced, browse & check out active brands, industry
              trends, & which type of posts perform best!
            </span>
          </div>
          <div className="flex w-full md:p-[20px] flex-col items-center gap-[20px] rounded-[8px] md:border-[1px] md:border-[solid] md:border-[#262626]">
            <div className="flex flex-col gap-[20px] w-full md:w-[555px]">
              <div
                className="flex border border-[#262626] gap-3 rounded-[10px] p-4 items-center hover:bg-[#1e1e1e] cursor-pointer"
                onClick={() => handleAddPastCollab()}
              >
                <PlusIcon size={24} />
                <span className="text-[14px] not-italic font-normal leading-[normal]">
                  Add your past collaboration
                </span>
              </div>
              <span className="text-start text-[14px]">
                Browse & get inspired by peers
              </span>

              {/* -------- loading skeleton ------------------- */}
              {loading && (
                <div className="animate-pulse flex flex-col gap-5">
                  <div className="w-full h-[200px] bg-[#262626] rounded-[10px]"></div>
                  <div className="w-full h-[200px] bg-[#262626] rounded-[10px]"></div>
                  <div className="w-full h-[200px] bg-[#262626] rounded-[10px]"></div>
                </div>
              )}

              {/* ----------- No post not found ------ */}
              {!loading && posts?.length === 0 && (
                <>
                  <div className="w-full px-[16px] py-[12px] flex justify-center items-center text-center max-w-[555px] bg-[#1e1e1e] text-white h-[300px] rounded-[10px] shadow-md">
                    <div>
                      <h3 className="text-2xl text-[#8e8e8e] font-bold">
                        No Collaboration Posts Found
                      </h3>
                      <p className="text-muted-foreground text-pretty text-sm">
                        It looks like there are no collaboration posts to
                        display at this time. <br className="hidden md:block" />{" "}
                        Please try again later.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* ------------------- posts ------------------- */}
              {!loading && posts?.length > 0 && (
                <div className="md:max-w-[555px] w-full flex flex-col gap-5">
                  {posts.map((post, index) => (
                    <FeedPostCard
                      key={index}
                      influencerName={post?.influencerData?.name}
                      influencerProfilePic={post?.influencerData?.profilePic}
                      brandName={post?.collabData?.companyName}
                      textContent={post?.postData?.postContent}
                      imageContent={post?.postData?.imageLink}
                      posturl={post?.collabData?.postLink}
                      isFollowing={post?.influencerData?.isFollowing}
                      isKudos={post?.postData?.isKudos}
                      onKudos={() =>
                        handleKudos(post?.collabData?.collabId, index)
                      }
                      onFollow={() =>
                        handleFollow(post?.influencerData?.influencerId, index)
                      }
                      videoLink={post?.postData?.videoLink}
                      onProfileImageClick={() =>
                        handleOpenProfile(
                          post?.influencerData?.name,
                          post?.influencerData?.slug,
                          index
                        )
                      }
                    />
                  ))}
                  {hasMore && (
                    <div className="flex border text-sm border-[#262626] gap-3 rounded-full justify-center p-2 items-center">
                      {!paginationLoading && hasMore && (
                        <div
                          className="cursor-pointer w-full text-center"
                          onClick={() => handleLoadMore()}
                        >
                          Load More
                        </div>
                      )}
                      {paginationLoading && (
                        <div className="cursor-wait">Loading...</div>
                      )}
                    </div>
                  )}
                  {!hasMore && (
                    <>
                      <div className="space-y-3">
                        <div className="text-xs w-full text-center">
                          That's a wrap for today! You've explored all the
                          amazing collaborations. <br />
                          Stay tuned for more exciting content coming your way
                          soon!
                        </div>
                        <div
                          className="flex border border-[#262626] gap-3 rounded-[10px] p-4 items-center hover:bg-[#1e1e1e] cursor-pointer"
                          onClick={() => handleAddPastCollab()}
                        >
                          <PlusIcon size={24} />
                          <span className="text-[14px] not-italic font-normal leading-[normal]">
                            Add your past collaboration
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="hidden md:block sticky top-10">
          <Leaderboard />
        </div>
      </div>
    </>
  );
};
